// ═════════════════════════════════════════════════════════════════════════════
// Auto tab — NHCX communication (payer-initiated document request)
//
// No submit form. The payer posts a query mid pre-auth/claim review; this
// screen lists cached entries (GET /responses, use_case=communication) and
// replies with text and/or attachments.
// Contract: COMMUNICATION_UI_INTEGRATION.md (repo root).
// ═════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useRef } from "react";
import { MenuItem } from "@mui/material";
import { C } from "../constants";
import IpdTextField from "../../ipd/utils/IpdTextField";
import IpdButton from "../../ipd/utils/IpdButton";
import SvgIcon from "../SvgIcon";
import {
  listCommunications,
  replyToCommunication,
  pollNhcxResponse,
  triggerDummyPayer,
  normalizeCommunicationError,
} from "../communication-api";

const INBOX_POLL_MS = 2000;
const INBOX_MAX_POLLS = 8;

const SAMPLE_REQUEST = {
  correlation_id: "preview-communication",
  use_case: "communication",
  status: "received",
  replied: false,
  sender_code: "1000003538@hcx",
  workflow_id: "24",
  parsed: {
    identifier: "f5b03f25-70bc-4dd6-b2c3-606f33385c8d",
    payload_strings: [
      "Implant bill for procedure document missing",
      "Hospital Fecilities missing - Bed capacity or Registration Details",
      "Care Team Details missing",
    ],
    based_on: [
      {
        reference: "urn:uuid:preview",
        claim_identifiers: [{ system: "https://irdai.gov.in", value: "7502a970-preview" }],
        claim_use: "preauthorization",
      },
    ],
    requester_display: "Dummy Payer",
    authored_on: "2026-08-26T13:23:01+05:30",
    status: "active",
    priority: "routine",
  },
  received_at: "2026-08-26T07:53:01Z",
};

const cardStyle = {
  background: C.card,
  borderRadius: "12px",
  border: `1px solid ${C.border}`,
  boxShadow: "0 2px 8px rgba(13,35,63,0.07)",
};
const innerCardStyle = {
  border: `1px solid ${C.border}`,
  borderRadius: "10px",
  padding: "16px",
  background: "#fff",
  display: "flex",
  flexDirection: "column",
  gap: "14px",
};
const labelStyle = {
  fontSize: "11px",
  fontWeight: "700",
  color: C.textSub,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  display: "block",
  lineHeight: 1.4,
  marginBottom: "6px",
};

function Pill({ tone = "grey", children }) {
  const tones = {
    grey: { bg: "#f1f3f5", fg: C.textSub, br: C.border },
    green: { bg: "#e9f7ef", fg: C.success, br: "#b7e4c7" },
    red: { bg: "#fdecec", fg: C.danger, br: "#f5c2c0" },
    blue: { bg: C.accent, fg: C.primary, br: "#bfe3fb" },
    amber: { bg: "#fff8e1", fg: C.warning, br: "#ffe69c" },
  };
  const t = tones[tone] || tones.grey;
  return (
    <span
      style={{
        fontSize: "10px",
        fontWeight: "700",
        padding: "3px 9px",
        borderRadius: "20px",
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.br}`,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function PreviewBanner() {
  return (
    <div
      style={{
        fontSize: "12px",
        fontWeight: "600",
        color: "#B45309",
        background: "#FFF8E1",
        border: "1px solid #FFE082",
        borderRadius: "8px",
        padding: "8px 12px",
      }}
    >
      Preview — sample data so you can see this screen. This is not a payer request.
    </div>
  );
}

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function parsedOf(item) {
  const parsed = item?.parsed;
  if (!parsed || parsed.error) return {};
  return parsed;
}

function payloadStringsOf(item) {
  const raw = parsedOf(item).payload_strings;
  return Array.isArray(raw) ? raw.filter(Boolean) : [];
}

function claimUseOf(item) {
  const basedOn = parsedOf(item).based_on;
  const first = Array.isArray(basedOn) ? basedOn[0] : null;
  return String(first?.claim_use || "");
}

function claimIdsOf(item) {
  const basedOn = parsedOf(item).based_on;
  const first = Array.isArray(basedOn) ? basedOn[0] : null;
  return Array.isArray(first?.claim_identifiers) ? first.claim_identifiers : [];
}

function isReplied(item) {
  return Boolean(item?.replied) || String(item?.status || "") === "replied";
}

function sortRequests(list) {
  return [...list].sort((a, b) => {
    const aOpen = isReplied(a) ? 1 : 0;
    const bOpen = isReplied(b) ? 1 : 0;
    if (aOpen !== bOpen) return aOpen - bOpen;
    return String(b.received_at || "").localeCompare(String(a.received_at || ""));
  });
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error("Could not read the file."));
    reader.readAsDataURL(file);
  });
}

export default function CommunicationPanel({
  onOpenPreauth,
  onOpenClaim,
  onContinueToPaymentNotice,
}) {
  const fileRef = useRef(null);
  const [requests, setRequests] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replies, setReplies] = useState([""]);
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendMessage, setSendMessage] = useState("");
  const [preview, setPreview] = useState(false);
  const [triggerCorrId, setTriggerCorrId] = useState("");
  const [triggerMethod, setTriggerMethod] = useState("Preauth");
  const [initing, setIniting] = useState(false);
  const [initMsg, setInitMsg] = useState("");

  const selected = requests.find((n) => n.correlation_id === selectedId) || null;
  const pendingCount = requests.filter((n) => !isReplied(n)).length;

  const seedReplies = (item) => {
    const asks = payloadStringsOf(item);
    setReplies(asks.length ? asks.map(() => "") : [""]);
    setAttachments([]);
  };

  const loadInbox = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const list = sortRequests(await listCommunications());
      setRequests((prev) => {
        const previewRows = prev.filter((n) => n.correlation_id === SAMPLE_REQUEST.correlation_id);
        const merged = [...previewRows, ...list.filter((n) => n.correlation_id !== SAMPLE_REQUEST.correlation_id)];
        return sortRequests(merged);
      });
      return list;
    } catch (err) {
      setError(normalizeCommunicationError(err, "list"));
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInbox();
  }, [loadInbox]);

  useEffect(() => {
    if (selectedId) return;
    const firstOpen = requests.find((n) => !isReplied(n));
    const first = firstOpen || requests[0];
    if (first?.correlation_id) {
      setSelectedId(first.correlation_id);
      seedReplies(first);
    }
  }, [requests, selectedId]);

  const handleSkip = () => {
    setPreview(true);
    setError(null);
    setSendMessage("");
    setRequests((prev) => {
      if (prev.some((n) => n.correlation_id === SAMPLE_REQUEST.correlation_id)) return prev;
      return sortRequests([SAMPLE_REQUEST, ...prev]);
    });
    setSelectedId(SAMPLE_REQUEST.correlation_id);
    seedReplies(SAMPLE_REQUEST);
  };

  const handleRefresh = () => {
    setSendMessage("");
    loadInbox();
  };

  const handlePickFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    try {
      const next = await Promise.all(
        files.map(async (file) => ({
          content_type: file.type || "application/octet-stream",
          title: file.name || "Attachment",
          language: "en-IN",
          data_base64: await fileToBase64(file),
        }))
      );
      setAttachments((prev) => [...prev, ...next]);
    } catch (err) {
      setError({ message: err?.message || "Could not read one of the files." });
    }
  };

  const handleReply = async () => {
    if (!selected?.correlation_id || sending) return;
    const contentStrings = replies.map((s) => String(s || "").trim()).filter(Boolean);
    if (!contentStrings.length && !attachments.length) {
      setError({ message: "Provide at least one attachment or one reply line." });
      return;
    }
    if (preview && selected.correlation_id === SAMPLE_REQUEST.correlation_id) {
      const now = new Date().toISOString();
      setRequests((prev) =>
        prev.map((n) =>
          n.correlation_id === selected.correlation_id
            ? { ...n, status: "replied", replied: true, replied_at: now }
            : n
        )
      );
      setSendMessage("Reply sent (preview). The decision still arrives on the original pre-auth or claim.");
      return;
    }
    setSending(true);
    setError(null);
    setSendMessage("");
    try {
      await replyToCommunication({
        correlationId: selected.correlation_id,
        contentStrings,
        attachments,
      });
      const confirmed = await pollNhcxResponse(selected.correlation_id);
      const next =
        confirmed?.pending
          ? { ...selected, status: "replied", replied: true, replied_at: new Date().toISOString() }
          : { ...selected, ...confirmed, correlation_id: selected.correlation_id };
      setRequests((prev) =>
        prev.map((n) => (n.correlation_id === selected.correlation_id ? next : n))
      );
      setSendMessage(
        "Documents sent to the payer. Watch the original pre-auth or claim — the decision lands there, not here."
      );
    } catch (err) {
      setError(normalizeCommunicationError(err));
    } finally {
      setSending(false);
    }
  };

  const handleDummyInit = async () => {
    if (!triggerCorrId.trim()) {
      setInitMsg("Paste the pre-auth or claim correlation id first.");
      return;
    }
    setIniting(true);
    setInitMsg("");
    setError(null);
    try {
      await triggerDummyPayer({
        action: "Query",
        method: triggerMethod,
        correlationId: triggerCorrId.trim(),
      });
      setInitMsg("Dummy payer triggered — watching the inbox for the document request.");
      let found = false;
      for (let n = 0; n < INBOX_MAX_POLLS; n += 1) {
        await new Promise((r) => setTimeout(r, INBOX_POLL_MS));
        const list = await loadInbox({ silent: true });
        const match = list.find((item) => !isReplied(item));
        if (match) {
          setSelectedId(match.correlation_id);
          seedReplies(match);
          found = true;
          break;
        }
      }
      setInitMsg(
        found
          ? "Document request received under a new correlation id."
          : "Still waiting — refresh the inbox in a moment if it has not appeared."
      );
    } catch (err) {
      setInitMsg(normalizeCommunicationError(err, "trigger").message);
    } finally {
      setIniting(false);
    }
  };

  const parsed = selected ? parsedOf(selected) : {};
  const asks = selected ? payloadStringsOf(selected) : [];
  const claimUse = selected ? claimUseOf(selected) : "";
  const claimIds = selected ? claimIdsOf(selected) : [];
  const parseFailed = Boolean(selected?.parsed?.error);
  const replied = selected ? isReplied(selected) : false;
  const priority = String(parsed.priority || "routine");
  const showPriority = priority && priority !== "routine";

  return (
    <div style={cardStyle}>
      <div
        style={{
          padding: "16px 20px",
          borderBottom: `1px solid ${C.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <SvgIcon name="send" size={16} color={C.primary} />
          <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "700", color: C.text }}>
            NHCX Communication · Payer document request
          </h3>
        </div>
        <Pill tone={pendingCount ? "amber" : "grey"}>
          {pendingCount ? `${pendingCount} unanswered` : "Inbox clear"}
        </Pill>
      </div>

      <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "16px" }}>
        {preview && <PreviewBanner />}

        <div style={{ fontSize: "13px", color: C.textSub }}>
          While a pre-auth or claim is under review the payer can ask for documents unprompted.
          That request blocks settlement until you answer. The reply itself is not a decision —
          the verdict still arrives on the original pre-auth or claim.
        </div>

        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <IpdButton
            variant="outlined"
            onClick={handleRefresh}
            disabled={loading}
            loading={loading}
            startIcon={<SvgIcon name="refresh" size={14} color={C.primary} />}
          >
            Refresh inbox
          </IpdButton>
          <IpdButton variant="outlined" onClick={handleSkip}>
            Skip — preview this screen
          </IpdButton>
        </div>

        {error && (
          <div style={{ border: "1px solid #f5c2c0", background: "#fdecec", borderRadius: "10px", padding: "14px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <SvgIcon name="alert-triangle" size={15} color={C.danger} />
              <span style={{ fontSize: "13px", fontWeight: "700", color: C.danger }}>{error.message}</span>
            </div>
            {(error.fieldErrors || []).map((fe, i) => (
              <div key={i} style={{ fontSize: "12px", color: C.text, marginTop: "6px" }}>
                {fe.field ? `${fe.field}: ` : ""}{fe.message}
              </div>
            ))}
            {error.status === 404 && (
              <div style={{ fontSize: "12px", color: C.text, marginTop: "6px" }}>
                Retry will not reconstruct a lost cache. Ask the payer to re-send the request.
              </div>
            )}
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "16px",
            alignItems: "start",
          }}
        >
          <div style={innerCardStyle}>
            <span style={{ fontSize: "12px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Inbox
            </span>
            {loading && requests.length === 0 ? (
              <div style={{ fontSize: "13px", color: C.textSub }}>Loading requests…</div>
            ) : requests.length === 0 ? (
              <div style={{ fontSize: "13px", color: C.textSub }}>
                No document requests cached. They arrive while a pre-auth or claim is under review,
                or trigger a Query from the sandbox panel.
              </div>
            ) : (
              requests.map((item) => {
                const done = isReplied(item);
                const active = item.correlation_id === selectedId;
                const lines = payloadStringsOf(item);
                const use = claimUseOf(item);
                return (
                  <button
                    key={item.correlation_id}
                    type="button"
                    onClick={() => {
                      setSelectedId(item.correlation_id);
                      setSendMessage("");
                      setError(null);
                      seedReplies(item);
                    }}
                    style={{
                      textAlign: "left",
                      cursor: "pointer",
                      border: `1px solid ${active ? C.primary : done ? C.border : "#FFE082"}`,
                      background: active ? C.accent : done ? "#fff" : "#FFF8E1",
                      borderRadius: "10px",
                      padding: "12px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "8px", alignItems: "center" }}>
                      <span style={{ fontSize: "13px", fontWeight: "800", color: C.text }}>
                        {parsedOf(item).requester_display || "Payer"}
                      </span>
                      <Pill tone={done ? "grey" : "amber"}>{done ? "Replied" : "Unanswered"}</Pill>
                    </div>
                    <div style={{ fontSize: "12px", color: C.text, lineHeight: 1.4 }}>
                      {lines[0] || "Document request"}
                      {lines.length > 1 ? ` · +${lines.length - 1} more` : ""}
                    </div>
                    <div style={{ fontSize: "11px", color: C.textSub }}>
                      {use || "claim"} · {formatWhen(item.received_at || parsedOf(item).authored_on) || "—"}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {!selected ? (
            <div style={{ ...innerCardStyle, alignItems: "center", textAlign: "center", padding: "40px 20px" }}>
              <SvgIcon name="send" size={26} color={C.primary} />
              <div style={{ fontSize: "15px", fontWeight: "700", color: C.text }}>No request selected</div>
              <div style={{ fontSize: "13px", color: C.textSub }}>
                Pick a request from the inbox, or skip to preview the reply screen.
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {parseFailed && (
                <div style={{ ...innerCardStyle, background: "#fdecec", borderColor: "#f5c2c0" }}>
                  <div style={{ fontSize: "13px", color: C.danger, fontWeight: "700" }}>
                    The request could not be parsed
                  </div>
                  <div style={{ fontSize: "12px", color: C.text }}>{selected.parsed.error}</div>
                </div>
              )}

              <div style={innerCardStyle}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                  <span style={{ fontSize: "12px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Payer asked
                  </span>
                  <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                    {showPriority && <Pill tone="amber">{priority}</Pill>}
                    <Pill tone={replied ? "grey" : "amber"}>{replied ? "Replied" : "Unanswered"}</Pill>
                  </div>
                </div>
                {asks.length > 0 ? (
                  <ol style={{ margin: 0, paddingLeft: "18px", fontSize: "13px", color: C.text, display: "flex", flexDirection: "column", gap: "6px" }}>
                    {asks.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ol>
                ) : (
                  <div style={{ fontSize: "13px", color: C.textSub }}>No itemised ask in the payload.</div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
                  <span style={{ color: C.textSub }}>Payer</span>
                  <span style={{ fontWeight: "700", color: C.text }}>{parsed.requester_display || "—"}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
                  <span style={{ color: C.textSub }}>About</span>
                  <span style={{ fontWeight: "700", color: C.text }}>{claimUse || "—"}</span>
                </div>
                {claimIds.map((id, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", gap: "10px" }}>
                    <span style={{ color: C.textSub }}>Claim id</span>
                    <span style={{ fontFamily: "monospace", color: C.text, wordBreak: "break-all" }}>{id.value || "—"}</span>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
                  <span style={{ color: C.textSub }}>Asked at</span>
                  <span style={{ fontWeight: "700", color: C.text }}>
                    {formatWhen(parsed.authored_on || selected.received_at) || "—"}
                  </span>
                </div>
                {parsed.identifier && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", gap: "10px" }}>
                    <span style={{ color: C.textSub }}>Payer ref</span>
                    <span style={{ fontFamily: "monospace", color: C.text, wordBreak: "break-all" }}>{parsed.identifier}</span>
                  </div>
                )}
              </div>

              <div style={innerCardStyle}>
                <span style={{ fontSize: "12px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Reply
                </span>
                {!replied ? (
                  <>
                    <div style={{ fontSize: "12px", color: C.textSub }}>
                      One line per item they asked for, plus any files. At least one of those is required.
                    </div>
                    {(asks.length ? asks : ["Reply"]).map((ask, i) => (
                      <div key={i}>
                        <label style={labelStyle}>{asks.length ? ask : "Note to payer"}</label>
                        <IpdTextField
                          fullWidth
                          value={replies[i] || ""}
                          onChange={(e) =>
                            setReplies((prev) => {
                              const next = [...prev];
                              next[i] = e.target.value;
                              return next;
                            })
                          }
                          placeholder="Your reply for this item"
                        />
                      </div>
                    ))}
                    <div>
                      <input
                        ref={fileRef}
                        type="file"
                        multiple
                        onChange={handlePickFiles}
                        style={{ display: "none" }}
                      />
                      <IpdButton
                        variant="outlined"
                        onClick={() => fileRef.current?.click()}
                        startIcon={<SvgIcon name="upload" size={14} color={C.primary} />}
                      >
                        Attach files
                      </IpdButton>
                    </div>
                    {attachments.map((att, i) => (
                      <div
                        key={`${att.title}-${i}`}
                        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", fontSize: "12px" }}
                      >
                        <span style={{ color: C.text, fontWeight: 600 }}>{att.title}</span>
                        <button
                          type="button"
                          onClick={() => setAttachments((prev) => prev.filter((_, idx) => idx !== i))}
                          style={{ border: "none", background: "transparent", cursor: "pointer", color: C.danger, fontWeight: 700 }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <IpdButton
                      onClick={handleReply}
                      disabled={sending}
                      loading={sending}
                      startIcon={<SvgIcon name="send" size={14} color="#fff" />}
                    >
                      {sending ? "Sending…" : "Send reply"}
                    </IpdButton>
                  </>
                ) : (
                  <div style={{ fontSize: "13px", fontWeight: "600", color: C.success }}>
                    Replied{selected.replied_at ? ` · ${formatWhen(selected.replied_at)}` : ""}. Kept here as the audit trail.
                  </div>
                )}
                {sendMessage && <div style={{ fontSize: "12px", color: C.text }}>{sendMessage}</div>}
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {claimUse === "claim"
                    ? onOpenClaim && (
                        <IpdButton variant="outlined" onClick={onOpenClaim} startIcon={<SvgIcon name="arrow-right" size={14} color={C.primary} />}>
                          Back to Claim
                        </IpdButton>
                      )
                    : onOpenPreauth && (
                        <IpdButton variant="outlined" onClick={onOpenPreauth} startIcon={<SvgIcon name="arrow-right" size={14} color={C.primary} />}>
                          Back to Pre-Auth
                        </IpdButton>
                      )}
                  {onContinueToPaymentNotice && (
                    <IpdButton variant="outlined" onClick={onContinueToPaymentNotice}>
                      Continue to Payment Notice
                    </IpdButton>
                  )}
                </div>
                <div style={{ fontSize: "10px", color: C.textSub, fontFamily: "monospace", wordBreak: "break-all", borderTop: `1px solid ${C.border}`, paddingTop: "10px" }}>
                  <div>correlation · {selected.correlation_id}</div>
                  {selected.received_at && <div>received · {selected.received_at}</div>}
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={{ ...innerCardStyle, background: "#fff8e1", borderColor: "#ffe69c" }}>
          <div style={{ fontSize: "12px", fontWeight: "800", color: C.text }}>Sandbox dummy payer</div>
          <div style={{ fontSize: "12px", color: C.textSub }}>
            The payer only raises a query if you ask it to. Submit a pre-auth or claim, wait ~10 seconds,
            then trigger Query. The request lands under a <strong>new</strong> correlation id — this inbox,
            not the original pre-auth id.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "12px" }}>
            <div>
              <label style={labelStyle}>Pre-auth / claim correlation id</label>
              <IpdTextField
                fullWidth
                value={triggerCorrId}
                onChange={(e) => setTriggerCorrId(e.target.value)}
                placeholder="from the pre-auth or claim submit"
              />
            </div>
            <div>
              <label style={labelStyle}>Method</label>
              <IpdTextField select fullWidth value={triggerMethod} onChange={(e) => setTriggerMethod(e.target.value)}>
                <MenuItem value="Preauth">Preauth</MenuItem>
                <MenuItem value="Claim">Claim</MenuItem>
              </IpdTextField>
            </div>
          </div>
          <div>
            <IpdButton variant="outlined" onClick={handleDummyInit} disabled={initing} loading={initing}>
              Trigger Query
            </IpdButton>
          </div>
          {initMsg && <div style={{ fontSize: "12px", color: C.text }}>{initMsg}</div>}
        </div>
      </div>
    </div>
  );
}
